import React, { useCallback, useState } from "react";
import {
  useTranslation,
  Box,
  Link,
  Flex,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  Text,
  Modal,
  ModalBody,
  Button,
} from "@familyzone/component-library";
import moment from "moment";
import ComponentLoading from "../../../modules/ComponentLoading";
import type { CommunicationCampaign, CommunicationsResponse } from "../../../types/Community";
import FileSaver from "file-saver";

const formatFrequency = (frequency?: CommunicationCampaign["frequency"]): string => {
  if (!frequency) return "-";
  const map: Record<CommunicationCampaign["frequency"], string> = {
    "one-off": "One-off",
    weekly: "Weekly",
    monthly: "Monthly",
  };
  return map[frequency];
};

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return "-";
  return moment(isoDate).format("D MMM YYYY");
};

interface CommunicationsTableProps {
  data: CommunicationsResponse;
  isLoading: boolean;
  isError: boolean;
}

// Keep this empty state in the component so error/no-data views
// can render `-` placeholders while API fields remain numeric.
const EMPTY_STATE_TABLE: Partial<CommunicationCampaign>[] = [
  {
    name: "Activity summary",
    frequency: "weekly",
    status: "inactive",
  },
  {
    name: "Invitation 2",
    frequency: "one-off",
    status: "inactive",
  },
  {
    name: "Invitation 1",
    frequency: "one-off",
    status: "inactive",
  },
];

// Parse the data to handle empty states for the table since
// the API doesn't return empty values. If no firstSentDate is provided,
// we assume there is no data.
function parseEmptyStates({ data }: CommunicationsResponse) {
  const result: Partial<CommunicationCampaign>[] = [];
  for (const campaign of data) {
    if (campaign.firstSentDate) {
      result.push(campaign);
      continue;
    }

    result.push({
      id: campaign.id,
      name: campaign.name,
      frequency: campaign.frequency,
      status: campaign.status,
      templateUrl: campaign.templateUrl,
      firstSentDate: undefined,
      openRate: undefined,
      signUps: undefined,
      signUpRate: undefined,
    });
  }
  return result;
}

export default function CommunicationsTable({ data, isLoading, isError }: CommunicationsTableProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<{ url: string; name: string } | null>(null);
  const [isPreviewUnavailable, setIsPreviewUnavailable] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const closeTemplatePreview = useCallback(() => {
    setSelectedTemplate(null);
    setIsPreviewUnavailable(false);
    setIsPreviewLoading(false);
  }, []);

  const openTemplatePreview = useCallback((url: string, name: string) => {
    setIsPreviewLoading(true);
    setIsPreviewUnavailable(false);
    setSelectedTemplate({
      url,
      name,
    });
  }, []);

  const downloadTemplate = useCallback(() => {
    if (!selectedTemplate) return;
    const cleanedName = selectedTemplate.name.trim().split(" ").filter(Boolean).join("_");
    FileSaver.saveAs(selectedTemplate.url, `${cleanedName || "template"}.png`);
  }, [selectedTemplate]);

  const columns = [
    { key: "name", label: t("Email") },
    { key: "firstSentDate", label: t("First sent") },
    { key: "frequency", label: t("Frequency") },
    { key: "status", label: t("Status") },
    { key: "openRate", label: t("Open rate") },
    { key: "signUps", label: t("Sign ups") },
    { key: "signUpRate", label: t("Sign up rate") },
    { key: "template", label: t("Template") },
  ];

  const parsedData = isError ? EMPTY_STATE_TABLE : parseEmptyStates(data);

  function StatusBadge({ status }: { status: CommunicationCampaign["status"] }) {
    const isActive = status === "active";
    return (
      <Flex align="center" gap="sp8">
        <Box width="8px" height="8px" borderRadius="50%" bg={isActive ? "accent.green.400" : "neutrals.60"} />
        <Text fontSize="md" color="text.title">
          {isActive ? t("Active") : t("Inactive")}
        </Text>
      </Flex>
    );
  }

  return (
    <Box bg="neutrals.0" p="sp24" borderRadius="md" boxShadow={30}>
      {/* Header */}
      <Box mb="sp16">
        <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title">
          {t("Communications")}
        </Text>
        <Text fontSize="md" color="text.title" mt="sp4">
          {t("An overview of your campaigns performance")}
        </Text>
      </Box>

      {/* Content */}
      {isLoading ? (
        <Flex justify="center" align="center" height="220px" data-testid="communications-table-loading">
          <ComponentLoading />
        </Flex>
      ) : (
        <Box overflowX="auto" borderRadius="sm" border="1px solid" borderColor="neutrals.30">
          <Table width="100%" variant="striped" border="0px" minW="700px">
            {/* Table Header */}
            <Thead>
              <Tr boxShadow={20} position="relative">
                {columns.map((col) => (
                  <Th
                    key={col.key}
                    textAlign="left"
                    py="sp24"
                    px="sp16"
                    color="text.title"
                    fontWeight="medium"
                    fontSize="md"
                    headerText={col.label}
                    aria-label={col.label}
                  />
                ))}
              </Tr>
            </Thead>

            {/* Table Body */}
            <Tbody>
              {parsedData.map((campaign: Partial<CommunicationCampaign>) => (
                <Tr key={`${campaign.id ?? ""}-${campaign.name ?? ""}`}>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {campaign.name}
                  </Td>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {formatDate(campaign.firstSentDate)}
                  </Td>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {formatFrequency(campaign.frequency)}
                  </Td>
                  <Td py="sp8" px="sp16" fontSize="md" verticalAlign="middle">
                    <StatusBadge status={campaign.status ?? "inactive"} />
                  </Td>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {parse(campaign.openRate)}%
                  </Td>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {parse(campaign.signUps)}
                  </Td>
                  <Td py="sp8" px="sp16" color="text.title" fontSize="md" verticalAlign="middle">
                    {parse(campaign.signUpRate)}%
                  </Td>
                  <Td py="sp8" px="sp16" fontSize="md" verticalAlign="middle">
                    {campaign.templateUrl && campaign.name ? (
                      <Link
                        href={campaign.templateUrl}
                        color="brand.500"
                        aria-label={t("View template")}
                        display="flex"
                        paddingY="sp8"
                        rounded="sm"
                        width="fit-content"
                        _hover={{ backgroundColor: "neutrals.20" }}
                        onClick={(event) => {
                          event.preventDefault();
                          // we know that the templateUrl and name are not null because we checked them above
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          openTemplatePreview(campaign.templateUrl!, campaign.name!);
                        }}
                      >
                        <Icon icon="fa-solid fa-arrow-up-right-from-square" marginX="24" />
                      </Link>
                    ) : (
                      <Text color="text.paragraph.regular">-</Text>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      <Modal
        headerText={t("Template preview")}
        isOpen={Boolean(selectedTemplate)}
        size="lg"
        onClose={closeTemplatePreview}
        contentProps={{ style: { maxHeight: "90vh", overflow: "hidden" } }}
      >
        <ModalBody>
          <Box
            border="1px solid"
            borderColor="neutrals.30"
            borderRadius="sm"
            bg="neutrals.10"
            p="sp16"
            maxHeight="calc(90vh - 220px)"
            overflowY="auto"
          >
            {selectedTemplate && !isPreviewUnavailable ? (
              <Box position="relative" minHeight="200px">
                {isPreviewLoading && (
                  <Flex justify="center" align="center" minHeight="200px" data-testid="template-preview-loading">
                    <ComponentLoading />
                  </Flex>
                )}
                <img
                  src={selectedTemplate.url}
                  alt={t("Template preview")}
                  style={{ width: "100%", height: "auto", display: isPreviewLoading ? "none" : "block" }}
                  onLoad={() => setIsPreviewLoading(false)}
                  onError={() => {
                    setIsPreviewLoading(false);
                    setIsPreviewUnavailable(true);
                  }}
                />
              </Box>
            ) : (
              <Text color="text.paragraph.regular">{t("Preview unavailable")}</Text>
            )}
          </Box>
          <Flex justify="flex-end" gap="sp8" mt="sp16" zIndex={10}>
            <Button variant="outline" size="compact" onClick={closeTemplatePreview}>
              {t("Close")}
            </Button>
            <Button variant="primary" size="compact" leftIcon={<Icon icon="fa-download" />} onClick={downloadTemplate}>
              {t("Download")}
            </Button>
          </Flex>
        </ModalBody>
      </Modal>
    </Box>
  );
}

function parse(n?: number) {
  if (n === undefined) return "-";
  return String(n);
}
